<template>
  <div class="flex flex-wrap items-center gap-1.5">
    <!-- Search, with suggestions from all of the user's jobs (not just the page that is loaded) -->
    <div class="relative flex-1 min-w-[10rem] sm:max-w-xs">
      <SearchSuggest v-model="search" :items="suggestions" :loading="suggesting" compact wide placeholder="Search jobs, companies, locations" empty-title="Latest jobs" />
    </div>

    <!-- Which jobs: all of them (the default), the bookmarked ones, the ignored ones, or the ones not acted on yet -->
    <FilterDropdown label="Show" :options="viewOptions" :model-value="filters.view" neutral="all" @update:model-value="$emit('change', { view: $event })" />

    <FilterDropdown label="Job title" :options="titleOptions" :model-value="filters.job_title_id" @update:model-value="$emit('change', { job_title_id: $event })" />
    <FilterDropdown label="Location" :options="locationOptions" :model-value="filters.job_location_id" @update:model-value="$emit('change', { job_location_id: $event })" />
    <FilterDropdown label="Status" :options="statusOptions" :model-value="filters.status" @update:model-value="$emit('change', { status: $event })" />
    <FilterDropdown label="AI resume" :options="AI_RESUME_OPTIONS" :model-value="filters.ai_resume" neutral="all" @update:model-value="$emit('change', { ai_resume: $event })" />
    <FilterDropdown label="Employer" :options="POSTER_OPTIONS" :model-value="filters.poster" neutral="all" @update:model-value="$emit('change', { poster: $event })" />
    <FilterDropdown label="Posted" :options="POSTED_OPTIONS" :model-value="filters.posted" neutral="" @update:model-value="$emit('change', { posted: $event })" />

    <span class="hidden md:block w-px h-4 bg-gray-200 dark:bg-gray-700 mx-0.5" aria-hidden="true"></span>

    <FilterDropdown label="Sort" :options="SORT_OPTIONS" :model-value="filters.sort" neutral="newest" @update:model-value="$emit('change', { sort: $event })" />

    <button v-if="active" type="button" @click="$emit('clear')"
      class="inline-flex items-center gap-1 h-8 px-2 text-[13px] font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300">
      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>
      Clear
    </button>
  </div>
</template>

<script setup>
import { computed, ref, watch, onBeforeUnmount } from 'vue'
import FilterDropdown from './FilterDropdown.vue'
import SearchSuggest from '../shared/SearchSuggest.vue'
import { useFetchedJobsStore } from 'stores/fetchedJobs.js'

const props = defineProps({
  filters: { type: Object, required: true },
  titles: { type: Array, default: () => [] },
  locations: { type: Array, default: () => [] },
  counts: { type: Object, required: true },
  active: { type: Boolean, default: false },
})
const emit = defineEmits(['change', 'clear'])

const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest posted' },
  { value: 'recently_saved', label: 'Recently saved' },
]

// Jobs with, or without, a resume AI wrote for them (the values are what the server accepts: all, available, missing).
const AI_RESUME_OPTIONS = [
  { value: 'all', label: 'All jobs' },
  { value: 'available', label: 'AI resume available' },
  { value: 'missing', label: 'No AI resume yet' },
]

// How recently a job was posted. The values are what the server accepts (FetchedJob::POSTED_WITHIN).
const POSTED_OPTIONS = [
  { value: '', label: 'Any time' },
  { value: '1h', label: 'Past hour' },
  { value: '24h', label: 'Past 24 hours' },
  { value: '7d', label: 'Past week' },
  { value: '30d', label: 'Past month' },
]

// Who posted the job. A company is a recruiter when the user marked it so, from its details (Job Listings) or on the Companies page.
const POSTER_OPTIONS = [
  { value: 'all', label: 'All employers' },
  { value: 'company', label: 'Individual companies' },
  { value: 'recruiter', label: 'Recruiters' },
]

const viewOptions = computed(() => [
  { value: 'all', label: `All jobs (${props.counts.all})` },
  { value: 'bookmarked', label: `Bookmarked (${props.counts.bookmarked})` },
  { value: 'ignored', label: `Ignored (${props.counts.ignored})` },
  { value: 'active', label: `Not actioned (${props.counts.active})` },
])

const titleOptions = computed(() => [{ value: '', label: 'All job titles' }, ...props.titles.map((title) => ({ value: title.id, label: title.name }))])
const locationOptions = computed(() => [{ value: '', label: 'All locations' }, ...props.locations.map((location) => ({ value: location.id, label: location.name }))])
const statusOptions = computed(() => [
  { value: 'all', label: 'All jobs' },
  { value: 'not_applied', label: `Not applied (${props.counts.all - props.counts.applied})` },
  { value: 'applied', label: `Applied (${props.counts.applied})` },
])

// Typing in the search box waits a moment, so the list is not reloaded on every key.
const search = ref(props.filters.q)
let timer = null

watch(search, (value) => {
  clearTimeout(timer)
  timer = setTimeout(() => emit('change', { q: value }), 300)
  askForSuggestions(value)
})

// The suggestions come from the server (the list on screen is only one page of the jobs), a moment after typing stops. Only the newest answer is used.
const store = useFetchedJobsStore()
const suggestions = ref([])
const suggesting = ref(false)
let suggestTimer = null
let suggestRequest = 0

function askForSuggestions(value) {
  clearTimeout(suggestTimer)
  suggesting.value = true
  suggestTimer = setTimeout(async () => {
    const request = ++suggestRequest
    const found = await store.suggest(value)
    if (request !== suggestRequest) return
    suggestions.value = found
    suggesting.value = false
  }, 200)
}

// Before anything is typed the box offers the latest jobs; a change of view (all, bookmarked, ignored, not actioned) makes them stale.
askForSuggestions('')
watch(() => props.filters.view, () => askForSuggestions(search.value))

// Clearing the filters elsewhere empties the box too.
watch(() => props.filters.q, (value) => { if (value !== search.value) search.value = value })

onBeforeUnmount(() => { clearTimeout(timer); clearTimeout(suggestTimer); suggestRequest += 1 })
</script>
